<template lang="pug">
.accordions.flex.flex-col.gap-2.items-stretch.justify-items-stretch
  div(
    class="has-[button:focus-visible]_ring-4"
    v-for="({ heading, ...card }, i) in accordions"
  )
    uc-animate.accordion.surface.rounded-xl.w-full(
      :animate="animate"
      :class="{ 'overflow-hidden': !isOpen(card.id) }"
      :id="card.id"
      :key="card.id"
      role="presentation"
    )
      audio-marker(:name="`${card.id}-accordion-${i}`")
      h4(
        uc-word-bold=""
      )
        .heading-container.transition-all(
          :class="[isOpen(card.id) ? 'rounded-t-xl' : 'rounded-xl', twMerge('bg-paper', screen.styles?.['accordion-heading'], card?.styles?.['accordion-heading'])]"
        )
          uc-preload-sfx(name="down")
          uc-preload-sfx(name="up")
          button.accordion__heading.flex.items-start.w-full.relative.p-4.px-6.gap.gap-6.group(
            :aria-controls="`${card.id}_content`"
            :aria-expanded="isOpen(card.id) ? 'true' : 'false'"
            :data-id="card.id"
            :data-index="i"
            :data-sfx="isOpen(card.id) ? 'down' : 'up'"
            @click="toggle"
          )
            uc-editable.prose.toggle-heading.max-w-prose.text-lg.font-semibold.dark_text-gray-200.transition-colors.m-0.text-start(
              :class="'!m-0 !p-0'"
              :editPath="`${editPath}.accordions.${i}.heading`"
              :k="`${card.id}.heading`"
              :text="heading"
              type="span"
            )

            span.text-2xl.ms-auto.me-1.my-2.h-4.inline-flex.items-center.justify-center.relative.transition-all.rounded-full.text-gray-500.dark_text-gray-200.group-hover_text-primary-500(
              :class="[twMerge(screen.styles?.['accordion-icon'], card?.styles?.['accordion-icon'])]"
            )
              transition-fade(:duration="0.2" :rotate="270")
                uc-icon.absolute.transition-colors(
                  :class="[twMerge(screen.styles?.['accordion-icon'], card?.styles?.['accordion-icon'])]"
                  :icon="isCompleted(card.id) ? 'theme:check' : 'theme:plus-circle'"
                  v-if="!isOpen(card.id)"
                )
              transition-fade(:duration="0.2" :rotate="-270")
                uc-icon.absolute(
                  :class="[twMerge(screen.styles?.['accordion-icon'], card?.styles?.['accordion-icon'])]"
                  icon="theme:minus-circle"
                  v-if="isOpen(card.id)"
                )

      .accordion__content.rounded-b-xl.overflow-hidden(
        :data-content-id="card.id"
        :id="`${card.id}_content`"
        :ref="setContentRef"
        :class="[isOpen(card.id) ? 'visible' : 'invisible', twMerge('bg-paper', screen.styles?.['accordion-content'], card?.styles?.['accordion-content'])]"
      )
        .p-4.px-6.pb-8
          uc-content(:editPath="`${editPath}.accordions.${i}`" :screen="card")
</template>

<script setup lang="ts">
import gsap from 'gsap';
import {
  inject,
  computed,
  reactive,
  onUpdated,
  onMounted,
  nextTick,
  ref,
  watch,
  toRef,
  withDefaults,
  type Ref
} from 'vue';
import { set, setCompleted } from '../lib/SCORM';
import { twMerge } from 'tailwind-merge';
import useSuspend from '../lib/useSuspend';
import { play, stop } from '../lib/audio';
import useTheme from '../stores/useTheme';
import isQuizPage from '../lib/isQuizPage';
import useScreenAudio from '../lib/useScreenAudio';
import accordionSchema from '../lib/screen-schemas/screen-accordion.schema';

const props = withDefaults(
  defineProps<{
    editPath: string;
    screen: { accordions: UcScreenRequiredId[] };
    /** Ancestor owns section intro when nested under `uc-screen` / `templates[]`. */
    nested?: boolean;
  }>(),
  { nested: false }
);
const accordions = computed(() => props.screen.accordions);
const theme = useTheme();
const suspend = useSuspend();
const wordDoc = inject('wordDoc') as Ref<boolean>;
const contentEls = reactive({});
const sectionId = inject('sectionId') as string;
const pageId = inject('pageId') as object;
const sectionActive = inject<Ref<boolean>>('sectionActive', ref(false));
const currentKey = ref<string>();

const animate = computed(
  () => props.screen['animate-accordion'] || theme.value['animate-accordion']
);

const { playSlot, queueSlot } = useScreenAudio(
  toRef(props, 'screen'),
  accordionSchema
);

const isOpen = id => wordDoc.value || 'accordion_' + id === currentKey.value;

const isCompleted = id => {
  const cardId = 'accordion_' + id;
  return Boolean(suspend.completed[cardId]);
};

const setContentRef = (el: HTMLElement) => {
  const id = el?.dataset?.contentId;

  if (id) {
    contentEls[id] = el;
  }

  return el;
};

const toggle = async (e: MouseEvent) => {
  const el = e.currentTarget as HTMLElement;
  const key = 'accordion_' + el.dataset.id;
  const i = Number(el.dataset.index);

  if (currentKey.value === key) {
    await stop({ fadeOut: 0.2 });
    currentKey.value = '';
  } else {
    currentKey.value = key;

    const card = accordions.value[i] as UcContent;
    const propAudio = card?.audio || card?.speech;
    await stop({ fadeOut: 0.2 });
    if (propAudio) {
      play(propAudio);
    } else {
      playSlot(`accordion-${i}`);
    }

    setCompleted(key);
    set(key, true);

    await nextTick();

    if (allCompleted()) {
      setCompleted(sectionId);
    }
  }
};

const allCompleted = () => {
  let allComplete = true;

  for (const { id } of accordions.value) {
    const cardId = 'accordion_' + id;
    if (!suspend.completed[cardId]) {
      allComplete = false;
      break;
    }
  }

  return allComplete;
};

const reset = () => {
  for (const { id } of accordions.value) {
    const cardId = 'accordion_' + id;
    set(`completed.${cardId}`, false);
  }
  currentKey.value = '';
};

const updateHeight = (id: string, isOpen: boolean) => {
  if (isOpen) {
    if (suspend.reducedMotion) {
      gsap.set(contentEls[id], { height: 'auto', autoAlpha: 1 });
    } else {
      gsap.to(contentEls[id], {
        height: 'auto',
        duration: 0.5,
        autoAlpha: 1,
        overwrite: true
      });
    }
  } else {
    if (suspend.reducedMotion) {
      gsap.set(contentEls[id], { height: 0, autoAlpha: 0 });
    } else {
      gsap.to(contentEls[id], {
        height: 0,
        duration: 0.2,
        autoAlpha: 0,
        overwrite: true
      });
    }
  }
};

// Schema audio: play intro on section activation; if an accordion is open, queue it too.
const playSectionAudio = async () => {
  await playSlot('intro');
  if (currentKey.value) {
    const idx = accordions.value.findIndex(
      a => 'accordion_' + a.id === currentKey.value
    );
    if (idx >= 0) {
      const card = accordions.value[idx] as UcContent;
      const propAudio = card?.audio || card?.speech;
      if (propAudio) {
        play(propAudio);
      } else {
        queueSlot(`accordion-${idx}`);
      }
    }
  }
};

watch(sectionActive, active => {
  if (props.nested || !active) return;
  void playSectionAudio();
});

// TODO - fold this into generic optional screen functionality. See UFD-225
const checkIfOptional = () => {
  if (props.screen['optional-accordion']) {
    setCompleted(sectionId);
  }
};

for (const { id } of accordions.value) {
  onMounted(() => {
    updateHeight(id, isOpen(id));
  });

  onUpdated(() => {
    updateHeight(id, isOpen(id));
  });
}

onMounted(async () => {
  checkIfOptional();

  // The first section on a page never receives a false→true sectionActive
  // transition, so play its intro here (mirrors uc-screen.vue).
  await nextTick();
  if (!props.nested && sectionActive.value) void playSectionAudio();
});

const app = document.querySelector('#app');
app?.addEventListener('quizReset', async e => {
  if (await isQuizPage(e, pageId)) reset();
});
</script>
